import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, PackagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ALIQUOTE, chiaveNome, parseNum, round2, toInput } from "@/lib/format";
import { SimpleSelect, btnGhost, btnPrimary } from "@/components/app/common";
import { CategoriaCombobox, catOpts } from "@/components/app/CategoriaCombobox";

const cell = "w-full h-9 rounded-lg border border-[#EAE3DC] bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C6D62]/30";
const NUOVA = "__nuova__";

const fromRiga = ({ i, riga }, cambio, sezione) => {
  const ap = riga.ai_proposta || {};
  const pack = sezione === "packaging";
  return {
  i, nome: (riga.descrizione || "").trim().slice(0, 120), tipo_articolo: pack ? "packaging" : "prodotto", ai: !!riga.ai_proposta,
  marca: pack ? "" : ap.marca_id || (ap.marca && ap.marca_nuova ? NUOVA : ""), nuovaMarca: !pack && ap.marca_nuova ? ap.marca : "",
  linea: (pack ? ap.sottotipo : ap.tipologia) || "", sku: "", ean: riga.ean || "", iva: riga.aliquota_iva ?? 22, prezzo: "", simile: null, link: false,
  costo: toInput(round2(((parseNum(riga.prezzo_unitario) || 0) * (1 - (parseNum(riga.sconto) || 0) / 100)) / (cambio || 1))),
  };
};

function MarcaCell({ r, marche, onChange, idx }) {
  const gia = r.marca === NUOVA && r.nuovaMarca.trim() && marche.find((m) => chiaveNome(m.nome) === chiaveNome(r.nuovaMarca));
  return (
    <div className="space-y-1.5">
      <SimpleSelect testId={`crea-marca-${idx}`} className="h-9" value={r.marca} onChange={(v) => onChange({ marca: v })} placeholder="Scegli marca"
        options={[{ value: NUOVA, label: "+ Nuova marca" }, ...marche.map((m) => ({ value: m.id, label: m.nome }))]} />
      {r.marca === NUOVA && <input data-testid={`crea-nuova-marca-${idx}`} className={cell} placeholder="Nome nuova marca" value={r.nuovaMarca} onChange={(e) => onChange({ nuovaMarca: e.target.value })} />}
      {gia && <div data-testid={`crea-marca-esistente-${idx}`} className="text-[11px] text-[#4E6656]">Esiste già «{gia.nome}»: verrà usata quella</div>}
    </div>
  );
}

// One brand per normalized name: reuse existing ones, create each new one once even if typed on many rows.
async function creaMarche(rows, marche) {
  const map = {};
  const byKey = Object.fromEntries(marche.map((m) => [chiaveNome(m.nome), m.id]));
  for (const nome of [...new Set(rows.filter((r) => r.marca === NUOVA).map((r) => r.nuovaMarca.trim()))]) {
    const k = chiaveNome(nome);
    if (!byKey[k]) byKey[k] = (await api.post("/marche", { nome }, { params: { riusa: true } })).data.id;
    map[nome] = byKey[k];
  }
  return map;
}

export function CreaProdottiDialog({ items, onClose, cambio, onCreated, sezione = "prodotti" }) {
  const [rows, setRows] = useState([]);
  const [marche, setMarche] = useState([]);
  const [linee, setLinee] = useState([]);
  const [packTip, setPackTip] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!items) return;
    setRows(items.map((x) => fromRiga(x, cambio, sezione)));
    api.post("/prodotti/simili", { descrizioni: items.map((x) => x.riga.descrizione || "") })
      .then((r) => setRows((rs) => rs.map((row, k) => (r.data[k] ? { ...row, simile: r.data[k], link: true } : row)))).catch(() => {});
    api.get("/marche").then((r) => {
      setMarche(r.data.filter((m) => m.attivo !== false));
      const byKey = Object.fromEntries(r.data.map((m) => [chiaveNome(m.nome), m.id]));
      setRows((rs) => rs.map((row) => (row.marca === NUOVA && byKey[chiaveNome(row.nuovaMarca)] ? { ...row, marca: byKey[chiaveNome(row.nuovaMarca)], nuovaMarca: "" } : row)));
    });
    api.get("/categorie", { params: { tipo: "linea" } }).then((r) => setLinee(r.data.map((c) => c.nome)));
    api.get("/categorie", { params: { tipo: "packaging" } }).then((r) => setPackTip(r.data.map((c) => c.nome)));
  }, [items, cambio, sezione]);
  const addTip = (c) => (c.tipo === "packaging" ? setPackTip : setLinee)((x) => [...x, c.nome]);
  const tipOf = (r) => (r.tipo_articolo === "packaging" ? packTip : linee);
  const upd = (k, patch) => setRows((rs) => rs.map((r, j) => (j === k ? { ...r, ...patch } : r)));
  const all = (patch) => setRows((rs) => rs.map((r) => ({ ...r, ...patch })));

  const save = async () => {
    const bad = rows.findIndex((r) => !r.link && (!r.nome.trim() || !r.linea || (!r.marca && r.tipo_articolo !== "packaging") || (r.marca === NUOVA && !r.nuovaMarca.trim()) || !(parseNum(r.costo) >= 0)));
    if (bad >= 0) return toast.error(`Riga ${rows[bad].i + 1}: completa nome, marca e tipologia`);
    setBusy(true);
    const created = [];
    try {
      const nuove = await creaMarche(rows.filter((r) => !r.link), marche);
      const fatte = new Set();
      for (const r of rows.filter((x) => !x.link && !tipOf(x).some((n) => chiaveNome(n) === chiaveNome(x.linea)))) {
        const tipo = r.tipo_articolo === "packaging" ? "packaging" : "linea";
        if (fatte.has(`${tipo}|${chiaveNome(r.linea)}`)) continue;
        fatte.add(`${tipo}|${chiaveNome(r.linea)}`);
        const { data } = await api.post("/categorie", { tipo, nome: r.linea }, { params: { riusa: true } });
        addTip({ tipo, nome: data.nome });
      }
      for (const r of rows) {
        if (r.link) { created.push({ i: r.i, prodotto: r.simile.prodotto, collegato: true }); continue; }
        const { data } = await api.post("/prodotti", { nome: r.nome.trim(), sku: r.sku.trim() || null, ean: r.ean.trim() || null, marca_id: r.marca === NUOVA ? nuove[r.nuovaMarca.trim()] : r.marca || null, linea: r.linea, tipo_articolo: r.tipo_articolo || "prodotto",
          costo_acquisto: parseNum(r.costo) || 0, prezzo_vendita: r.tipo_articolo === "packaging" ? 0 : parseNum(r.prezzo) || 0, aliquota_iva: Number(r.iva) });
        created.push({ i: r.i, prodotto: data });
      }
      onCreated(created);
      onClose();
    } catch (e) {
      if (created.length) onCreated(created);
      toast.error(`Riga ${rows[created.length]?.i + 1}: ${errMsg(e)}`);
      setRows((rs) => rs.slice(created.length));
    } finally { setBusy(false); }
  };

  const multi = rows.length > 1;
  return (
    <Dialog open={!!items} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-6xl rounded-2xl" data-testid="crea-prodotti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackagePlus className="h-5 w-5 text-[#8C6D62]" />{multi ? `Crea ${rows.length} prodotti mancanti` : "Crea prodotto dalla riga"}</DialogTitle>
          <DialogDescription>Dati precompilati dalla fattura: controllali. Il prodotto verrà abbinato alla riga e l'abbinamento salvato per le prossime fatture del fornitore.</DialogDescription>
        </DialogHeader>
        {multi && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#756B64]" data-testid="crea-applica-tutti">
            Applica a tutti:
            <SimpleSelect testId="crea-marca-tutti" className="h-9 w-52" value="" onChange={(v) => all({ marca: v })} placeholder="Marca" options={[{ value: NUOVA, label: "+ Nuova marca" }, ...marche.map((m) => ({ value: m.id, label: m.nome }))]} />
            <div className="w-48"><CategoriaCombobox testId="crea-linea-tutti" className="h-9" tipo="linea" value="" onChange={(v) => all({ linea: v })} placeholder="Tipologia" options={linee.map((l) => ({ value: l, label: l }))} onCreated={addTip} /></div>
          </div>
        )}
        <div className="overflow-auto max-h-[55vh] -mx-1 px-1">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{["Riga", "Nome prodotto", "Marca", "Tipo / tipologia", "SKU", "EAN", "Costo unit. €", "Prezzo vendita €", "IVA"].map((h) => <th key={h} className="text-left font-semibold px-1.5 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {rows.map((r, k) => (
                <Fragment key={r.i}>
                {r.simile && (
                  <tr data-testid={`crea-simile-${k}`} className="bg-[#8AA090]/10">
                    <td colSpan={9} className="px-2 py-2 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" data-testid={`crea-simile-link-${k}`} checked={r.link} onChange={(e) => upd(k, { link: e.target.checked })} className="accent-[#4E6656] h-4 w-4" />
                        <span>Riga {r.i + 1}: esiste già un prodotto simile: <b>{r.simile.prodotto.nome}</b>{r.simile.prodotto.marca ? ` (${r.simile.prodotto.marca})` : ""} · {Math.round(r.simile.punteggio * 100)}% — <b>abbina a questo</b> invece di crearne uno nuovo</span>
                      </label>
                    </td>
                  </tr>
                )}
                <tr data-testid={`crea-row-${k}`} className={`align-top ${r.link ? "opacity-40 pointer-events-none" : ""}`}>
                  <td className="px-1.5 py-2 text-xs text-[#756B64] pt-4">{r.i + 1}{r.ai && <span data-testid={`crea-ai-${k}`} title="Marca e tipologia proposte dall'AI: controlla e modifica se serve" className="block mt-1 text-[9px] font-bold px-1 py-0.5 rounded-full bg-[#E8C5C8]/60 text-[#77594E] text-center">AI</span>}</td>
                  <td className="px-1.5 py-2 min-w-[220px]"><input data-testid={`crea-nome-${k}`} className={cell} value={r.nome} onChange={(e) => upd(k, { nome: e.target.value })} /></td>
                  <td className="px-1.5 py-2 w-48"><MarcaCell r={r} marche={marche} idx={k} onChange={(p) => upd(k, p)} /></td>
                  <td className="px-1.5 py-2 w-40 space-y-1.5">
                    <select data-testid={`crea-tipo-${k}`} className={cell} value={r.tipo_articolo || "prodotto"} onChange={(e) => upd(k, { tipo_articolo: e.target.value, linea: "" })}><option value="prodotto">Prodotto</option><option value="packaging">Packaging</option></select>
                    <CategoriaCombobox key={r.tipo_articolo || "prodotto"} testId={`crea-linea-${k}`} className="h-9" tipo={r.tipo_articolo === "packaging" ? "packaging" : "linea"} value={r.linea} onChange={(v) => upd(k, { linea: v })} placeholder="Tipologia" options={tipOf(r).map((l) => ({ value: l, label: l }))} onCreated={addTip} />
                  </td>
                  <td className="px-1.5 py-2 w-32"><input data-testid={`crea-sku-${k}`} className={cell} value={r.sku} placeholder="Facoltativo" onChange={(e) => upd(k, { sku: e.target.value })} /></td>
                  <td className="px-1.5 py-2 w-32"><input data-testid={`crea-ean-${k}`} inputMode="numeric" className={cell} value={r.ean} placeholder="Facoltativo" onChange={(e) => upd(k, { ean: e.target.value })} /></td>
                  <td className="px-1.5 py-2 w-24"><input data-testid={`crea-costo-${k}`} inputMode="decimal" className={`${cell} text-right`} value={r.costo} onChange={(e) => upd(k, { costo: e.target.value })} /></td>
                  <td className="px-1.5 py-2 w-28"><input data-testid={`crea-prezzo-${k}`} inputMode="decimal" className={`${cell} text-right`} value={r.tipo_articolo === "packaging" ? "" : r.prezzo} disabled={r.tipo_articolo === "packaging"} placeholder={r.tipo_articolo === "packaging" ? "—" : "Facoltativo"} onChange={(e) => upd(k, { prezzo: e.target.value })} /></td>
                  <td className="px-1.5 py-2 w-20"><select data-testid={`crea-iva-${k}`} className={cell} value={r.iva} onChange={(e) => upd(k, { iva: Number(e.target.value) })}>{ALIQUOTE.map((a) => <option key={a} value={a}>{a}%</option>)}</select></td>
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {rows.some((r) => !r.link && !r.sku.trim()) && <div className="flex gap-2 text-xs text-[#8A6A33] bg-[#CBA368]/15 rounded-xl px-3 py-2" data-testid="crea-sku-warning"><AlertTriangle className="h-4 w-4 shrink-0" />Senza SKU il prodotto non potrà essere abbinato alle vendite Shopify: puoi aggiungerlo anche dopo in Prodotti.</div>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className={btnGhost} onClick={onClose} disabled={busy} data-testid="crea-annulla">Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={busy || !rows.length} data-testid="crea-salva">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : rows.every((r) => r.link) ? "Abbina" : multi ? `Crea e abbina ${rows.length} prodotti` : "Crea e abbina"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
